/**
 * Défi live : une seule fenêtre par compte (migration 0674).
 *
 * Chaque fenêtre tire un identifiant au hasard. À l'ouverture elle PREND la
 * main (la dernière ouverte gagne), puis vérifie toutes les 4 s qu'elle l'a
 * encore (RPC `live_session_ping`). Si une autre fenêtre (ou un autre
 * appareil) a pris la main, `superseded` passe à vrai : le player affiche la
 * fenêtre d'interruption ; « Jouer ici » reprend la main.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export const LIVE_SESSION_PING_MS = 4_000;

export type LiveSessionStatus = 'active' | 'superseded' | 'none';

/** Lit la réponse de la RPC (toute autre valeur = on réessaiera). PUR. */
export function parseSessionStatus(value: unknown): LiveSessionStatus {
  return value === 'active' || value === 'superseded' ? value : 'none';
}

/** Faut-il demander la main à ce passage ? (1re fois, ou pas encore inscrit). PUR. */
export function shouldClaim(claimed: boolean, last: LiveSessionStatus | null): boolean {
  return !claimed || last === 'none';
}

function newSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Repli (très vieux navigateurs) : uuid v4 depuis Math.random.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const db = supabase as unknown as SupabaseClient;

async function ping(challengeId: string, sessionId: string, claim: boolean) {
  const { data, error } = await db.rpc('live_session_ping', {
    p_challenge: challengeId,
    p_session: sessionId,
    p_claim: claim,
  });
  if (error) throw error;
  return parseSessionStatus(data);
}

export function useLiveSessionLock({
  challengeId,
  enabled,
}: {
  challengeId: string | null;
  /** Connecté, défi connu et pas terminé. */
  enabled: boolean;
}) {
  const sessionId = useMemo(newSessionId, []);
  const [superseded, setSuperseded] = useState(false);
  const claimedRef = useRef(false);
  const lastRef = useRef<LiveSessionStatus | null>(null);

  useEffect(() => {
    if (!enabled || !challengeId || superseded) return;
    let cancelled = false;
    const tick = async () => {
      const claim = shouldClaim(claimedRef.current, lastRef.current);
      try {
        const status = await ping(challengeId, sessionId, claim);
        if (cancelled) return;
        lastRef.current = status;
        if (status === 'active') claimedRef.current = true;
        if (status === 'superseded') setSuperseded(true);
      } catch {
        // réseau : on réessaie au prochain passage
      }
    };
    void tick();
    const id = setInterval(() => void tick(), LIVE_SESSION_PING_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled, challengeId, sessionId, superseded]);

  /** « Jouer ici » : cette fenêtre reprend la main (l'autre sera coupée). */
  const takeOver = useCallback(async () => {
    if (!challengeId) return;
    try {
      const status = await ping(challengeId, sessionId, true);
      lastRef.current = status;
      claimedRef.current = status === 'active';
      if (status !== 'superseded') setSuperseded(false);
    } catch {
      // on laisse la fenêtre d'interruption : l'élève peut réessayer
    }
  }, [challengeId, sessionId]);

  /** Le serveur a refusé une réponse (409 superseded). */
  const markSuperseded = useCallback(() => setSuperseded(true), []);

  return { sessionId, superseded, takeOver, markSuperseded };
}

/** Réponse 409 « superseded » d'une edge function (supabase-js FunctionsHttpError). */
export async function isSupersededError(error: unknown): Promise<boolean> {
  const ctx = (error as { context?: unknown } | null)?.context;
  if (!ctx || typeof (ctx as Response).clone !== 'function') return false;
  const res = ctx as Response;
  if (res.status !== 409) return false;
  try {
    const body = (await res.clone().json()) as { error?: unknown };
    return body?.error === 'superseded';
  } catch {
    return false;
  }
}
